import React, { useState, useEffect } from 'react';
import { breakRulesAPI } from '../../services/api';
import { Pause, Plus, Edit2, Trash2, X, Play, Info, CheckCircle, XCircle } from 'lucide-react';
import './BreakRulesManagement.css';

interface BreakRule {
  id: number;
  minWorkHours: number;
  breakMinutes: number;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const BreakRulesManagement: React.FC = () => {
  const [breakRules, setBreakRules] = useState<BreakRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<BreakRule | null>(null);
  const [formData, setFormData] = useState({
    minWorkHours: '',
    breakMinutes: '',
    description: ''
  });

  useEffect(() => {
    loadBreakRules();
  }, []);

  const loadBreakRules = async () => {
    try {
      console.log('🔄 Loading break rules...');
      const response = await breakRulesAPI.getBreakRules();
      console.log('✅ Break rules loaded:', response.data);
      
      // ALLE Regeln anzeigen - Filter wurde entfernt
      setBreakRules(response.data.breakRules);
    } catch (error: any) {
      console.error('❌ Error loading break rules:', error);
      console.error('Error details:', error.response?.data);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Pausenregeln' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const data = {
        minWorkHours: parseFloat(formData.minWorkHours),
        breakMinutes: parseInt(formData.breakMinutes),
        description: formData.description || undefined,
        sortOrder: breakRules.length
      };

      console.log('🔄 Submitting break rule:', data);

      if (editingRule) {
        console.log('✏️ Updating rule:', editingRule.id);
        await breakRulesAPI.updateBreakRule(editingRule.id, data);
        setMessage({ type: 'success', text: 'Pausenregel erfolgreich aktualisiert' });
      } else {
        console.log('➕ Creating new rule');
        await breakRulesAPI.createBreakRule(data);
        setMessage({ type: 'success', text: 'Pausenregel erfolgreich erstellt' });
      }

      setFormData({ minWorkHours: '', breakMinutes: '', description: '' });
      setShowModal(false);
      setEditingRule(null);
      await loadBreakRules();
    } catch (error: any) {
      console.error('❌ Submit error:', error);
      console.error('Error response:', error.response?.data);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Speichern der Pausenregel'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rule: BreakRule) => {
    setEditingRule(rule);
    setFormData({
      minWorkHours: rule.minWorkHours.toString(),
      breakMinutes: rule.breakMinutes.toString(),
      description: rule.description || ''
    });
    setShowModal(true);
  };

  const handleRowClick = (rule: BreakRule) => {
    handleEdit(rule);
  };

  const handleCreate = () => {
    setEditingRule(null);
    setFormData({ minWorkHours: '', breakMinutes: '', description: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRule(null);
    setFormData({ minWorkHours: '', breakMinutes: '', description: '' });
  };

  /* const resetForm = () => {
    closeModal();
  }; */

  const handleDelete = async (rule: BreakRule) => {
    if (!confirm(`Pausenregel "${rule.minWorkHours}h → ${rule.breakMinutes}min" wirklich löschen?`)) {
      return;
    }

    try {
      await breakRulesAPI.deleteBreakRule(rule.id);
      setMessage({ type: 'success', text: 'Pausenregel erfolgreich gelöscht' });
      loadBreakRules();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Löschen der Pausenregel'
      });
    }
  };

  const toggleActive = async (rule: BreakRule, e: React.MouseEvent) => {
    e.stopPropagation(); // Verhindere dass das Row-Click-Event ausgelöst wird
    try {
      await breakRulesAPI.updateBreakRule(rule.id, { isActive: !rule.isActive });
      setMessage({ 
        type: 'success', 
        text: `Pausenregel ${rule.isActive ? 'deaktiviert' : 'aktiviert'}` 
      });
      loadBreakRules();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Ändern der Pausenregel'
      });
    }
  };

  return (
    <div className="break-rules-management">
      <div className="break-rules-header">
        <h2>
          <Pause size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Pausenregeln Verwaltung
        </h2>
        <button 
          className="btn primary"
          onClick={handleCreate}
        >
          <Plus size={16} style={{ marginRight: '4px' }} />
          Neue Pausenregel
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button 
            className="message-close" 
            onClick={() => setMessage({ type: '', text: '' })}
          >
            <X />
          </button>
        </div>
      )}

      {/* Rules List */}
      <div className="break-rules-list">
        <h3>Pausenregeln</h3>
        
        {breakRules.length === 0 ? (
          <div className="no-rules">
            <p>Noch keine Pausenregeln definiert.</p>
            <p>Erstellen Sie Regeln, um automatische Pausenabzüge zu konfigurieren.</p>
          </div>
        ) : (
          <div className="rules-table">
            <table>
              <thead>
                <tr>
                  <th>Arbeitsstunden</th>
                  <th>Pausenminuten</th>
                  <th>Beschreibung</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {breakRules.map((rule) => (
                  <tr 
                    key={rule.id} 
                    className={!rule.isActive ? 'inactive' : ''}
                    onClick={() => handleRowClick(rule)}
                    style={{ cursor: 'pointer' }}
                    title="Klicken zum Bearbeiten"
                  >
                    <td>ab {rule.minWorkHours}h</td>
                    <td>{rule.breakMinutes} min</td>
                    <td>{rule.description || '-'}</td>
                    <td>
                      <span className={`status-badge ${rule.isActive ? 'active' : 'inactive'}`}>
                        {rule.isActive ? (
                          <>
                            <CheckCircle size={16} /> Aktiv
                          </>
                        ) : (
                          <>
                            <XCircle size={16} /> Inaktiv
                          </>
                        )}
                      </span>
                    </td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn-icon edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(rule);
                        }}
                        title="Bearbeiten"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className={`btn-icon ${rule.isActive ? 'deactivate' : 'activate'}`}
                        onClick={(e) => toggleActive(rule, e)}
                        title={rule.isActive ? 'Deaktivieren' : 'Aktivieren'}
                      >
                        {rule.isActive ? <Pause size={16} /> : <Play size={16} />}
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(rule);
                        }}
                        title="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="info-box">
        <h4>
          <Info size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
          Funktionsweise
        </h4>
        <ul>
          <li>Pausenregeln werden automatisch beim Ausstempeln angewendet</li>
          <li>Die höchste zutreffende Regel wird verwendet (z.B. bei 8h Arbeit wird die 6h-Regel angewendet, nicht die 9h-Regel)</li>
          <li>Pausenabzüge werden in der Arbeitszeit-Übersicht transparent angezeigt</li>
          <li>Regeln können jederzeit aktiviert/deaktiviert werden</li>
        </ul>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content break-rule-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingRule ? (
                  <>
                    <Edit2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Pausenregel bearbeiten
                  </>
                ) : (
                  <>
                    <Plus size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Neue Pausenregel erstellen
                  </>
                )}
              </h3>
              <button className="modal-close" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Mindest-Arbeitsstunden:</label>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="24"
                      value={formData.minWorkHours}
                      onChange={(e) => setFormData({ ...formData, minWorkHours: e.target.value })}
                      placeholder="z.B. 6"
                      required
                    />
                    <small>Ab wieviel Stunden soll die Pause abgezogen werden?</small>
                  </div>
                  
                  <div className="form-group">
                    <label>Pausenminuten:</label>
                    <input
                      type="number"
                      min="0"
                      max="480"
                      value={formData.breakMinutes}
                      onChange={(e) => setFormData({ ...formData, breakMinutes: e.target.value })}
                      placeholder="z.B. 30"
                      required
                    />
                    <small>Wieviel Minuten sollen abgezogen werden?</small>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Beschreibung (optional):</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="z.B. Mittagspause"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-actions">
                  <button type="button" className="btn secondary" onClick={closeModal}>
                    Abbrechen
                  </button>
                  <button type="submit" className="btn success" disabled={loading}>
                    {loading ? 'Speichern...' : editingRule ? 'Aktualisieren' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};